/**
 * RestorationPolicy (AJIP-16.02.004 · WS6)
 * ---------------------------------------------------------------------
 * Deterministic restoration pipeline. Restoration NEVER bypasses the
 * Core Engine or its validation surface. Every gate below must pass
 * before the record is fed to engine.restore().
 *
 *   1. Feature flags enabled  (checked by the calling PersistenceService)
 *   2. Journey definition registered
 *   3. Schema validated
 *   4. Checksum verified
 *   5. Not expired
 *   6. Ownership matches
 *   7. Migration to current version (if applicable)
 *   8. Current state is a valid state for the journey
 *
 * Gate failures raise the appropriate typed error and never leak raw
 * exceptions or storage-layer errors to the caller.
 */
import { verifyChecksum } from './PersistedRecord';
import {
  RecordExpiredError,
  SchemaValidationError,
  IntegrityError,
} from './PersistenceErrors';
import { assertOwnership } from './OwnershipGuard';

const runRestorationGates = ({
  record,
  registry,
  retentionPolicy,
  principalRef,
  nowMs,
}) => {
  // Gate 2 — Definition registered.
  const definition = registry.get(record.journeyDefinitionId); // throws NotRegisteredError

  // Gate 3 — Schema validated.
  //   validation is enforced by deserialise() upstream; nothing else here.

  // Gate 4 — Checksum verified.
  verifyChecksum(record);

  // Gate 5 — Expiry.
  if (retentionPolicy.isExpired(record, nowMs)) {
    throw new RecordExpiredError('Record has expired', {
      persistenceRecordId: record.persistenceRecordId,
      expiresAt: record.expiresAt,
    });
  }

  // Gate 6 — Ownership.
  assertOwnership(record, principalRef);

  // Gate 8 — currentState must be listed in the journey's availableStates.
  if (!definition.availableStates.includes(record.currentState)) {
    throw new SchemaValidationError(
      `currentState '${record.currentState}' is not available on journey '${definition.identifier}'`,
    );
  }

  return Object.freeze({ definition });
};

// Build the snapshot object accepted by engine.restore().
const toEngineSnapshot = (record) => Object.freeze({
  identifier: record.journeyDefinitionId,
  currentState: record.currentState,
  currentStage: record.currentStage,
  progress: record.progress,
  metadata: Object.freeze({ ...record.authorisedData }),
});

// Guard for tests to see that IntegrityError is re-exported here.
const RESTORATION_ERROR_TYPES = Object.freeze([
  'RecordExpiredError',
  'SchemaValidationError',
  'IntegrityError',
  'OwnershipError',
  'NotRegisteredError',
]);

export { runRestorationGates, toEngineSnapshot, RESTORATION_ERROR_TYPES };
